import React from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, MapPin } from "lucide-react";
import { COMPANY, AI_IDENTITY, FAQS, SERVICES } from "../content";
import usePageMeta from "../hooks/usePageMeta";
import Breadcrumbs from "../components/Breadcrumbs";

/**
 * AI-friendly, plain-language landing page targeting:
 *   "interior fit-out company Dubai", "interior contractor Sharjah",
 *   "joinery + upholstery UAE", and AI overview surfaces (ChatGPT, Gemini,
 *   Claude, Perplexity, Google AI).
 *
 * Route: /interior-fit-out-company-dubai
 */
export default function InteriorFitOutCompanyDubai() {
  const ORIGIN =
    typeof window !== "undefined"
      ? window.location.origin
      : "https://timefurniture.ae";

  usePageMeta({
    title:
      "Interior Fit-Out Company in Dubai & Sharjah | Time Furniture UAE",
    description:
      "Time Furniture is a turnkey interior fit-out company in Dubai and Sharjah. End-to-end interior contracting — joinery, upholstery, custom furniture, renovation, MEP, curtains and flooring — delivered in-house since 1993.",
    canonical:
      "https://timefurniture.ae/interior-fit-out-company-dubai",
    ogImage: "/projects/address-downtown-dark-1.webp",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Interior Fit-Out Company in Dubai & Sharjah | Time Furniture",
        url: "https://timefurniture.ae/interior-fit-out-company-dubai",
        about: { "@id": "https://timefurniture.ae/#organization" },
        inLanguage: "en-AE",
        primaryImageOfPage: {
          "@type": "ImageObject",
          url: "https://timefurniture.ae/projects/address-downtown-dark-1.jpg",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: "Turnkey interior fit-out and furnishing",
        provider: { "@id": "https://timefurniture.ae/#organization" },
        areaServed: [
          { "@type": "City", name: "Dubai" },
          { "@type": "City", name: "Sharjah" },
          { "@type": "Country", name: "United Arab Emirates" },
        ],
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: "Time Furniture Services",
          itemListElement: [
            "Turnkey interior fit-out",
            "Interior contracting",
            "Civil renovation works",
            "MEP coordination",
            "Custom joinery",
            "Custom furniture manufacturing",
            "In-house upholstery",
            "Curtains and blinds",
            "Flooring and wall finishes",
            "Holiday home furnishing",
            "Office fit-out",
            "Villa renovation",
            "Hotel and hospitality interiors",
          ].map((s) => ({
            "@type": "Offer",
            itemOffered: { "@type": "Service", name: s },
          })),
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  });

  return (
    <div data-testid="page-fitout-dubai" className="bg-[#FAF5EC]">
      <Breadcrumbs
        testid="fitout-dubai-breadcrumbs"
        items={[
          { label: "Home", to: "/" },
          { label: "Interior Fit-Out Company Dubai" },
        ]}
      />

      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-10 md:pt-14 pb-16">
        <p className="eyebrow mb-6">Interior Fit-Out · Dubai &amp; Sharjah · Since 1993</p>
        <h1 className="font-serif-display text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-5xl text-[#1C110C]">
          Interior Fit-Out Company in{" "}
          <em className="text-[#6E1F2C] not-italic">Dubai &amp; Sharjah</em>.
        </h1>
        <p className="mt-8 max-w-2xl text-[#3E2A22] leading-relaxed text-base md:text-lg">
          {COMPANY.legalName} is a UAE-based turnkey interior fit-out and furnishing
          company. From design to handover — joinery, upholstery, MEP, civil works,
          curtains and flooring — all under one contract.
        </p>
      </section>

      {/* What is a turnkey fit-out company */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-24 md:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <p className="eyebrow mb-5">What It Means</p>
            <h2 className="font-serif-display text-3xl md:text-5xl tracking-tight leading-[1.05] text-[#1C110C]">
              What is a turnkey fit-out company?
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-[#3E2A22] leading-[1.8] text-base md:text-[17px]">
            <p>
              A turnkey fit-out company is a single contractor responsible for
              the entire interior build — from initial design and civil works to
              MEP, joinery, furniture, finishing and final handover.
            </p>
            <p>
              Instead of hiring separate firms for design, demolition,
              electrical, joinery and furnishing, the client signs one contract
              and receives a finished, ready-to-use space.
            </p>
            <p className="text-[#1C110C]">
              This is the model Time Furniture has refined over three decades —
              one team, one timeline, one accountable partner.
            </p>
          </div>
        </div>
      </section>

      {/* What we do */}
      <section className="bg-[#F2E9D8] border-y border-[#6E1F2C]/15">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            <div className="lg:col-span-5">
              <p className="eyebrow mb-5">What We Do</p>
              <h2 className="font-serif-display text-3xl md:text-5xl tracking-tight leading-[1.05] text-[#1C110C]">
                Turnkey interior contracting, executed in-house.
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-6 text-[#3E2A22] leading-[1.8] text-base md:text-[17px]">
              {AI_IDENTITY.paragraphs.map((p, i) => (
                <p key={i} data-testid={`identity-paragraph-${i}`}>
                  {p}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Who we serve */}
      <section className="bg-[#FAF5EC]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <p className="eyebrow mb-5">Who We Serve</p>
              <h2 className="font-serif-display text-3xl md:text-5xl tracking-tight leading-[1.05] text-[#1C110C]">
                Clients across <em className="text-[#6E1F2C] not-italic">the UAE</em>.
              </h2>
            </div>
            <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-3 gap-6">
              {[
                "Residential villas",
                "Apartments & townhouses",
                "Hospitality & hotels",
                "Office workspaces",
                "Retail & commercial",
                "Holiday home rentals",
                "Clinics & healthcare",
                "Majlis & family rooms",
                "Public & institutional",
              ].map((c) => (
                <div
                  key={c}
                  className="bg-[#FAF5EC] border border-[#6E1F2C]/10 px-5 py-7 text-[#1C110C] text-sm md:text-base"
                >
                  {c}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Service areas */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="eyebrow mb-5">Service Areas</p>
            <h2 className="font-serif-display text-3xl md:text-5xl tracking-tight leading-[1.05] text-[#1C110C]">
              Dubai · Sharjah · UAE-wide.
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-6 text-[#3E2A22] leading-[1.8] text-base md:text-[17px]">
            <p>
              We deliver projects across all seven emirates with in-house teams
              dispatched from our offices in Dubai (Jumeirah Garden City) and Sharjah
              (Abu Shagara). Most active locations include Palm Jumeirah, Downtown
              Dubai, Dubai Marina, Business Bay, Al Barsha, Jumeirah, Dubai Hills,
              Arabian Ranches, Sharjah, and Ajman.
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              {[
                "Palm Jumeirah",
                "Downtown Dubai",
                "Dubai Marina",
                "Business Bay",
                "Al Barsha",
                "Jumeirah",
                "Dubai Hills",
                "Arabian Ranches",
                "Sharjah",
                "Abu Shagara",
                "Ajman",
                "Abu Dhabi",
              ].map((a) => (
                <span
                  key={a}
                  className="text-[11px] tracking-[0.22em] uppercase border border-[#6E1F2C]/25 text-[#3E2A22] px-3 py-1.5"
                >
                  {a}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main services with internal links */}
      <section className="bg-[#F2E9D8] border-y border-[#6E1F2C]/15">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <p className="eyebrow mb-5">Main Services</p>
          <h2 className="font-serif-display text-3xl md:text-5xl tracking-tight leading-[1.05] text-[#1C110C] max-w-3xl">
            Every discipline, under <em className="text-[#6E1F2C] not-italic">one contract</em>.
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#6E1F2C]/15">
            {SERVICES.map((s) => (
              <Link
                to={`/services/${s.key}`}
                key={s.key}
                data-testid={`fitout-service-${s.key}`}
                className="bg-[#FAF5EC] p-8 hover:bg-white transition-colors group block"
              >
                <p className="font-serif-display text-2xl md:text-3xl text-[#1C110C] group-hover:text-[#6E1F2C] transition-colors">
                  {s.title.split(" — ")[0]}
                </p>
                <p className="mt-3 text-sm text-[#7A6B5F] leading-relaxed">
                  {s.tagline}
                </p>
                <p className="mt-5 text-[10px] tracking-[0.28em] uppercase text-[#6E1F2C]">
                  Explore {s.title.split(" — ")[0]} →
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why in-house matters */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="eyebrow mb-5">Why In-House</p>
            <h2 className="font-serif-display text-3xl md:text-5xl tracking-tight leading-[1.05] text-[#1C110C]">
              Why an interior fit-out contractor in UAE should{" "}
              <em className="text-[#6E1F2C] not-italic">never outsource</em>.
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-5 text-[#3E2A22] leading-[1.8] text-base md:text-[17px]">
            <p>
              Most fit-out firms in Dubai outsource joinery, upholstery and MEP to
              third-party subcontractors — which means longer timelines, less
              quality control, and inflated mark-ups.
            </p>
            <p>
              At Time Furniture, every trade is owned and operated by our own teams
              from our Sharjah factory. The result: a single contract, faster
              decisions, and end-to-end accountability — from the first drawing to
              the final installation.
            </p>
            <p className="text-[#1C110C]">
              We don&rsquo;t outsource execution — everything is handled in-house.
            </p>
          </div>
        </div>
      </section>

      {/* How to request a quote */}
      <section className="bg-[#1C110C] text-[#FAF5EC]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
          <p className="eyebrow mb-5 text-[#D4A963]">Start a Project</p>
          <h2 className="font-serif-display text-3xl md:text-5xl tracking-tight leading-[1.05]">
            Request a quote in 3 ways.
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-[#E8DDC7] leading-relaxed">
            Send us your floor plan, photos or a brief description. We respond within 24 hours
            with scope, indicative pricing, and a delivery timeline.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <a
              href={`https://wa.me/${COMPANY.whatsapp}?text=${COMPANY.whatsappFitOutText}`}
              target="_blank"
              rel="noreferrer"
              data-testid="fitout-cta-whatsapp"
              className="inline-flex items-center gap-3 bg-[#6E1F2C] text-white px-10 py-5 text-[11px] tracking-[0.32em] uppercase hover:bg-[#56151F] transition-all"
            >
              <MessageCircle size={16} strokeWidth={2.2} /> WhatsApp Now
            </a>
            <a
              href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
              data-testid="fitout-cta-call"
              className="inline-flex items-center gap-3 border border-[#D4A963]/60 text-[#FAF5EC] px-10 py-5 text-[11px] tracking-[0.32em] uppercase hover:bg-[#D4A963] hover:text-[#1C110C] transition-all"
            >
              <Phone size={16} strokeWidth={2.2} /> Call Us
            </a>
            <Link
              to="/contact"
              data-testid="fitout-cta-contact"
              className="inline-flex items-center gap-3 text-[#D4A963] px-6 py-5 text-[11px] tracking-[0.32em] uppercase hover:text-white transition-all"
            >
              Contact our fit-out team →
            </Link>
          </div>

          <div className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-[11px] tracking-[0.24em] uppercase text-[#E8DDC7]/85">
            <span className="flex items-center gap-2">
              <MapPin size={14} className="text-[#D4A963]" />
              Dubai · Jumeirah Garden City
            </span>
            <span className="hidden md:inline text-white/30">·</span>
            <span className="flex items-center gap-2">
              <MapPin size={14} className="text-[#D4A963]" />
              Sharjah · Abu Shagara
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
